import { useCallback, useEffect, useRef, useState } from 'react';
import { headerApi } from 'app/services/headerApi';
import {
  LAST_GENERATED_REPORT_KEY,
  PENDING_REPORTS_KEY,
  expandFormatlessRows,
  purgePendingReportsOnce,
  readPendingReports,
  reconcilePendingReports,
  removePendingReportAt,
  subscribePendingReports,
  writePendingReports,
  type PendingReportRow,
} from 'app/pendingReports';
import type { IReportNotification } from 'models';

const {
  useGetReportsCountQuery,
  useGetReportsNotificationsQuery,
  useMarkReportReadMutation,
} = headerApi;

/**
 * Reports history dropdown (§3.A "Reports dropdown" / parity 107-111).
 *
 * Markup mirrors the Backbone `#reports-wrapper-nav` + `reports_list.tmpl`:
 *   span.reports-wrapper-nav#reports-wrapper-nav
 *     ├── a.report-button[href=/reports target=blank]   (unread badge anchor)
 *     ├── button.generated-reports-count[.hide]          (red/green count badge)
 *     └── span.generated-reports-list[.hide]             (the history panel)
 *
 * Behavior:
 *  - Unread count from GET /reports/count_reports_notification/1/
 *    (X-Notification-Count header) on a 30s poll (the ONLY HTTP poll). A count
 *    CHANGE re-fetches page 1 of the list (longPoll, header.js:749-768).
 *  - Clicking the count badge toggles `.generated-reports-list.hide`
 *    (openCloseList).
 *  - History list from GET /reports/reports_notifications/1/?page=N (50/page),
 *    infinite-scrolled (checkScroll: load next page within 150px of the bottom
 *    while page*50 <= loaded length).
 *  - Per-report Open / Mark as read / Share (openPDF / markAsRead / shareReport).
 *    Open opens download_link in a new tab + (if unread) decrements the badge +
 *    PATCHes read; Mark as read decrements + PATCHes; Share copies the URL and
 *    shows "Copied!" for 2s.
 *  - `.short-list` (no workflow button → dashboard case) is applied to the list.
 *
 * Phase 5b — optimistic pending rows (localStorage 'pending_reports'):
 *  - written by the reports board's Generate (action.js:112-181), surfaced
 *    here via the same-tab CustomEvent('storage') / cross-tab StorageEvent
 *    (header.js:361-370 → updateNotificationsList :770-775): rows render
 *    on TOP as `.not-ready` (report.tmpl:1-8) and the badge gets
 *    `.status-pending` (:772).
 *  - every list arrival reconciles pending ↔ arrived (getReportsList
 *    :734-747 → compareNotifications :834-853): matched rows clear; the rest
 *    persist (gaining allow_remove → the Delete action, :791 + report.tmpl:
 *    29-41 + removePendingReport :899-910).
 *  - localStorage 'last_generated_report' tracks the newest arrived id
 *    (:734-737) — the board stamps it into new rows as last_id.
 */
const PAGE_SIZE = 50;
const POLL_MS = 30000;

interface ReportsDropdownProps {
  /** Whether the panel is open (lifted to the header so only one nav-list shows). */
  open: boolean;
  onToggle: () => void;
}

const ReportsDropdown = ({ open, onToggle }: ReportsDropdownProps) => {
  // 30s poll for the unread count.
  const { data: count = 0 } = useGetReportsCountQuery(undefined, {
    pollingInterval: POLL_MS,
    refetchOnMountOrArgChange: true,
  });

  // Local optimistic count (decremented on open/mark-read like the Backbone view,
  // which mutates `this.reportsCount` directly). Re-synced when the poll changes it.
  const [localCount, setLocalCount] = useState(count);
  useEffect(() => setLocalCount(count), [count]);

  // Accumulated pages (infinite scroll).
  const [page, setPage] = useState(1);
  const [reports, setReports] = useState<IReportNotification[]>([]);
  const [readIds, setReadIds] = useState<Set<number>>(new Set());
  const loadingRef = useRef(false);

  const { data, isFetching, refetch } = useGetReportsNotificationsQuery(page, {
    refetchOnMountOrArgChange: true,
  });
  const [markReportRead] = useMarkReportReadMutation();

  // Pending "generating report..." rows (5b).
  const [pending, setPending] = useState<PendingReportRow[]>(() => {
    purgePendingReportsOnce(); // header.js:194-197
    return readPendingReports();
  });

  // updateNotificationsList (header.js:770-775) — rows written by the board
  // (same tab: CustomEvent; other tabs: native StorageEvent).
  useEffect(() => subscribePendingReports(setPending), []);

  // Which row's more-actions dropdown is open (dropdownToggle,
  // header.js:1123-1128 — opening one closes the others).
  const [moreOpenId, setMoreOpenId] = useState<string | null>(null);
  const toggleMore = useCallback((id: string) => {
    setMoreOpenId((cur) => (cur === id ? null : id));
  }, []);

  // longPoll (header.js:749-768): a changed unread count re-fetches page 1.
  const prevCountRef = useRef<number | null>(null);
  useEffect(() => {
    if (prevCountRef.current != null && count !== prevCountRef.current) {
      if (page === 1) void refetch();
      else setPage(1);
    }
    prevCountRef.current = count;
  }, [count, page, refetch]);

  // Merge fetched pages into the accumulated list.
  useEffect(() => {
    if (!data) return;
    setReports((prev) => (data.page === 1 ? data.rows : [...prev, ...data.rows]));
    loadingRef.current = false;
  }, [data]);

  // getReportsList tail (header.js:734-747): newest arrived id + reconcile
  // the pending rows against the (re)loaded list.
  useEffect(() => {
    if (reports.length) {
      localStorage.setItem(LAST_GENERATED_REPORT_KEY, String(reports[0].id));
    }
    if (localStorage.getItem(PENDING_REPORTS_KEY)) {
      const still = reconcilePendingReports(reports, readPendingReports());
      writePendingReports(still); // compareNotifications :843-851
      setPending(still);
    } else {
      setPending([]);
    }
  }, [reports]);

  const listBodyRef = useRef<HTMLUListElement | null>(null);

  // checkScroll: load the next page within 150px of the bottom (header.js).
  const onListScroll = useCallback(() => {
    const el = listBodyRef.current;
    if (!el) return;
    const bottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    if (bottom > 150 || loadingRef.current || page * PAGE_SIZE > reports.length) return;
    loadingRef.current = true;
    setPage((p) => p + 1);
  }, [page, reports.length]);

  const decrement = useCallback(() => {
    setLocalCount((c) => Math.max(0, c - 1));
  }, []);

  const isUnread = useCallback(
    (r: IReportNotification) => !r.is_read && !readIds.has(r.id),
    [readIds]
  );

  // openPDF: open download_link + (if unread) decrement + mark read.
  const onOpen = useCallback(
    (r: IReportNotification) => {
      if (isUnread(r)) {
        decrement();
        setReadIds((s) => new Set(s).add(r.id));
        markReportRead({ id: r.id });
      }
      if (r.download_link) window.open(r.download_link, '_blank', 'noreferrer');
    },
    [isUnread, decrement, markReportRead]
  );

  // markAsRead: decrement + PATCH read (no navigation).
  const onMarkRead = useCallback(
    (r: IReportNotification) => {
      if (!isUnread(r)) return;
      decrement();
      setReadIds((s) => new Set(s).add(r.id));
      markReportRead({ id: r.id });
    },
    [isUnread, decrement, markReportRead]
  );

  // shareReport: copy the full URL + show "Copied!" for 2s.
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const onShare = useCallback((r: IReportNotification) => {
    if (!r.download_link) return;
    const fullUrl = window.location.origin + r.download_link;
    navigator.clipboard?.writeText(fullUrl).then(() => {
      setCopiedId(r.id);
      setTimeout(() => setCopiedId((cur) => (cur === r.id ? null : cur)), 2000);
    });
  }, []);

  // removePendingReport (header.js:899-910).
  const onDeletePending = useCallback((index: number) => {
    setPending(removePendingReportAt(index));
    setMoreOpenId(null);
  }, []);

  const formatOf = (link?: string | null, fallback?: string): string => {
    if (link) {
      const clean = link.split('?')[0];
      const ext = clean.split('.').pop();
      if (ext && ext.length <= 5) return ext;
    }
    return fallback ?? '';
  };

  // renderPendingReports format expansion (header.js:859-909) — rows written
  // without a format fan out per available format, and a multi-page FMI row is
  // rewritten to 'zip' (persisted when changed).
  const [pendingRows, pendingChanged] = expandFormatlessRows(pending);
  useEffect(() => {
    if (pendingChanged) writePendingReports(pendingRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingChanged, pending]);

  const hasReports = reports.length > 0;
  const hasPending = pendingRows.length > 0;

  return (
    <span className="reports-wrapper-nav" id="reports-wrapper-nav">
      <a
        href="/reports"
        target="blank"
        rel="noreferrer"
        className="report-button icon-ic-report-black-24px ka-has-tooltip"
        tooltip="Reports"
        tabIndex={-1}
      />{' '}
      {/* Badge visibility mirrors Backbone renderReports: shown iff reports are
          loaded (or the unread poll count is non-zero before the list arrives,
          or pending rows exist). `.status-pending` while rows are generating
          (updateNotificationsList :772 / compareNotifications :840,851). */}
      <button
        className={`generated-reports-count${
          hasReports || hasPending || localCount > 0 ? '' : ' hide'
        }${hasPending ? ' status-pending' : ''}`}
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggle();
        }}
      >
        {localCount}
      </button>{' '}
      <span
        className={`generated-reports-list short-list${
          open && (hasReports || hasPending) ? '' : ' hide'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <ul className="reports-list">
          <li className="title-block-li with_bg">
            Recent Reports History
            <span
              className="close-history"
              role="button"
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
            >
              CLOSE
            </span>
          </li>
          <li className="title-block-li">
            <span className="notification_project">PROJECT</span>
            <span className="notification_report">REPORT</span>
            <span className="notification_format">FORMAT</span>
            <span className="notification_user">BY</span>
            <span className="notification_generated_on">GENERATED ON</span>
            <span className="notification_open_pdf" />
          </li>
        </ul>
        <ul className="reports-list reports-list-body" ref={listBodyRef} onScroll={onListScroll}>
          {/* Pending rows sit on TOP; the legacy prepends in iteration order,
              so the stored order renders reversed (renderPendingReports
              :862-877 + report.tmpl:1-8,29-41). */}
          {pendingRows
            .map((row, index) => ({ row, index }))
            .reverse()
            .map(({ row, index }) => (
              <li
                key={`pending-${index}`}
                data-index={index}
                className={`report-li unread not-ready${row.allow_remove ? ' allow_remove' : ''}`}
              >
                <span className="notification_project">{row.project_name}</span>
                <span className="notification_report">{row.report}</span>
                <span className="notification_format">{row.format}</span>
                <span className="notification_user">{row.user_initials}</span>
                <span className="notification_generated_on">{row.finished_at}</span>
                {row.allow_remove && (
                  <div className="with-border-radius notification_open_pdf">
                    <button
                      id={String(index)}
                      className="dropdown-toggle icon-more-actions-report report-list-btn-more"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMore(`pending-${index}`);
                      }}
                    />
                    <div
                      className={`dropdown-open top${
                        moreOpenId === `pending-${index}` ? ' active' : ''
                      }`}
                      id={`dropdown_${index}`}
                    >
                      <button
                        name="delete"
                        className="ka-tooltip-show-after delete-pending-report"
                        data-index={index}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeletePending(index);
                        }}
                      >
                        <i className="icon-trash" />
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          {reports.map((r) => {
            const unread = isUnread(r);
            return (
              <li
                key={r.id}
                id={String(r.id)}
                data-id={r.id}
                data-url={r.download_link ?? ''}
                className={`report-li ${unread ? 'unread' : 'read'}`}
                onClick={() => onMarkRead(r)}
                onDoubleClick={() => onOpen(r)}
              >
                <span className="notification_project">{r.project_name}</span>
                <span className="notification_report">{r.report}</span>
                <span className="notification_format">{formatOf(r.download_link, r.format)}</span>
                <span className="notification_user">{r.user_initials}</span>
                <span className="notification_generated_on">{r.finished_at}</span>
                <div className="with-border-radius notification_open_pdf">
                  <button
                    id={String(r.id)}
                    className="dropdown-toggle icon-more-actions-report report-list-btn-more"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMore(String(r.id));
                    }}
                  />
                  <div
                    className={`dropdown-open top${moreOpenId === String(r.id) ? ' active' : ''}`}
                    id={`dropdown_${r.id}`}
                  >
                    <button
                      data-id={r.id}
                      id="open_pdf"
                      tooltip="Open"
                      name="open"
                      className={`ka-tooltip-show-after open_pdf_button${unread ? ' unread' : ''}`}
                      data-url={r.download_link ?? ''}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpen(r);
                      }}
                    >
                      <i className="icon-open" />
                      Open
                    </button>
                    <button
                      data-id={r.id}
                      id="mark_as_read"
                      tooltip="Mark as read"
                      name="mark_read"
                      className={`ka-tooltip-show-after ${unread ? 'unread' : 'non-clickable'}`}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onMarkRead(r);
                      }}
                    >
                      <i className="icon-mark-email-read" /> Mark as read
                    </button>
                    <button
                      data-id={r.id}
                      name="share_report"
                      className="ka-tooltip-show-after share-report-btn"
                      data-url={r.download_link ?? ''}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onShare(r);
                      }}
                    >
                      {copiedId === r.id ? (
                        <>
                          <i className="icon-share-1" /> Copied!
                        </>
                      ) : (
                        <>
                          <i className="icon-share-1" /> Share
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
          {isFetching && reports.length === 0 && (
            <li className="report-li" style={{ padding: '12px', color: '#8c8c8c' }}>
              Loading...
            </li>
          )}
        </ul>
      </span>
    </span>
  );
};

export default ReportsDropdown;
